/**
 * Fan chart for a country's nowcast timeseries.
 *
 *   - 68% band via p16/p84
 *   - 90% band via p5/p95
 *   - Real-time median as a line on top
 *   - Long-run trend overlay
 *   - NBER recession shading via `getRecessionShapes`
 *
 * Click a point on the median trace to open the delta modal.
 */
import { useMemo } from 'react';
import type { Data, Layout, PlotMouseEvent } from 'plotly.js';
import PlotlyChart from '@/lib/plotly/PlotlyChart';
import { getRecessionShapes } from '@/lib/plotly/recessionShapes';
import { hexToRgba } from '@/lib/plotly/colors';
import { hoverTemplate } from '@/lib/plotly/hoverTemplates';
import { autoRangeYAxisMulti } from '@/lib/plotly/yAxisRange';
import { chartLayout } from '@/lib/plotly/layoutPresets';
import { AXIS_LINE } from '@/lib/plotly/defaults';
import { shortName } from '../lib/countries';
import { processNowcastData } from '../lib/processNowcasts';
import type { CountryIso, NowcastRow } from '../types';

export interface NowcastChartProps {
  country: CountryIso;
  rows: NowcastRow[];
  /** Called with (dateStr, p50Value) when the user clicks a median-line point. */
  onPointClick?: (date: string, value: number | null) => void;
  /** Override the default chart height (530px). Dashboard uses 380px. */
  height?: number;
}

/**
 * The Sept 14–19 2025 week is excluded from the y-axis autoscale because of
 * outlier data that would otherwise blow out the default y-range.
 */
const Y_AXIS_EXCLUDE_START = new Date('2025-09-14');
const Y_AXIS_EXCLUDE_END = new Date('2025-09-19');

function withinExcludedWindow(dateStr: string): boolean {
  const date = new Date(dateStr);
  return date >= Y_AXIS_EXCLUDE_START && date <= Y_AXIS_EXCLUDE_END;
}

function tickConfig(yearsSpan: number): { tickformat: string; dtick: string } {
  if (yearsSpan >= 25) return { tickformat: '%Y', dtick: 'M60' };
  if (yearsSpan >= 4) return { tickformat: '%Y', dtick: 'M12' };
  return { tickformat: '%b-%y', dtick: 'M2' };
}

const REALTIME_TRACE_NAME = 'Real-Time';

// Fan-band colors. Outer alpha = fanAlpha (0.6) * 0.6.
const OUTER_BAND_HEX = '#CCF9F0';
const INNER_BAND_HEX = '#66F0CC';
const BAND_LINE_RGBA = 'rgba(153, 153, 153, 0.3)';

export default function NowcastChart({
  country,
  rows,
  onPointClick,
  // Default 550 matches the 550px outer wrapper in EconomicOutlookPage.
  // The dashboard mini passes 380 explicitly.
  height = 550,
}: NowcastChartProps): JSX.Element {
  const processed = useMemo(() => processNowcastData(rows), [rows]);

  const { traces, layout } = useMemo(() => {
    if (processed.dates.length === 0) {
      return { traces: [] as Data[], layout: {} as Partial<Layout> };
    }

    const { dates, p5, p16, p50, p84, p95, trend } = processed;

    // Build fan-chart traces bottom-up so the upper bands fill toward the lower.
    // legendrank controls visible legend order (lower = earlier); render order
    // (bands behind line) is controlled by trace insertion order.
    const outerLower: Data = {
      x: dates,
      y: p5,
      mode: 'lines',
      line: { width: 0.5, color: BAND_LINE_RGBA },
      hoverinfo: 'skip',
      showlegend: false,
      name: '90% Confidence (lower)',
      type: 'scatter',
    };
    const outerUpper: Data = {
      x: dates,
      y: p95,
      mode: 'lines',
      fill: 'tonexty',
      fillcolor: hexToRgba(OUTER_BAND_HEX, 0.36),
      line: { width: 0.5, color: BAND_LINE_RGBA },
      hoverinfo: 'skip',
      name: '90% Confidence',
      type: 'scatter',
      legendrank: 3,
    };
    const innerLower: Data = {
      x: dates,
      y: p16,
      mode: 'lines',
      line: { width: 0.5, color: BAND_LINE_RGBA },
      hoverinfo: 'skip',
      showlegend: false,
      name: '68% Confidence (lower)',
      type: 'scatter',
    };
    const innerUpper: Data = {
      x: dates,
      y: p84,
      mode: 'lines',
      fill: 'tonexty',
      fillcolor: hexToRgba(INNER_BAND_HEX, 0.6),
      line: { width: 0.5, color: BAND_LINE_RGBA },
      hoverinfo: 'skip',
      name: '68% Confidence',
      type: 'scatter',
      legendrank: 2,
    };
    const trendX: string[] = [];
    const trendY: number[] = [];
    for (let i = 0; i < dates.length; i += 1) {
      const value = trend[i];
      if (value !== null && Number.isFinite(value)) {
        trendX.push(dates[i]);
        trendY.push(value);
      }
    }
    const trendTrace: Data | null =
      trendX.length > 0
        ? {
            x: trendX,
            y: trendY,
            mode: 'lines',
            name: 'Long-Run Trend',
            line: { color: '#000000', width: 2, dash: '5px,3px' as unknown as 'dash' },
            hovertemplate: hoverTemplate({ name: true, format: '.2f', suffix: '%' }),
            type: 'scatter',
            legendrank: 4,
          }
        : null;
    const realtime: Data = {
      x: dates,
      y: p50,
      mode: 'lines',
      name: REALTIME_TRACE_NAME,
      line: { color: '#0066FF', width: 2 },
      hovertemplate: hoverTemplate({ name: true, format: '.2f', suffix: '%' }),
      type: 'scatter',
      legendrank: 0, // Appears first in legend
    };

    // Render order: bands first (so lines paint on top), then trend, then
    // real-time line. Visible legend order is controlled by legendrank above:
    // Long-Run Trend, 68% Confidence, 90% Confidence, Real-Time.
    const assembled: Data[] = [outerLower, outerUpper, innerLower, innerUpper];
    if (trendTrace) assembled.push(trendTrace);
    assembled.push(realtime);

    // y-axis auto-range: use inner band (p16/p84) + trend, excluding the
    // hardcoded Sept 2025 week that is known to blow out the scale.
    const [yMin, yMax] = autoRangeYAxisMulti([p16, p84, trend], dates, {
      excludePredicate: (d) => withinExcludedWindow(d),
    });

    // x-axis range with one month of padding on the right. `dates` is sorted
    // ascending by `processNowcastData`, so first/last are the bounds.
    const minDate = new Date(dates[0]);
    const maxDate = new Date(dates[dates.length - 1]);
    const yearsSpan = (maxDate.getTime() - minDate.getTime()) / (1000 * 60 * 60 * 24 * 365.25);
    const xRangeEnd = new Date(maxDate);
    xRangeEnd.setDate(xRangeEnd.getDate() + 30);
    const { tickformat, dtick } = tickConfig(yearsSpan);

    let latestMedian = 0;
    for (let i = p50.length - 1; i >= 0; i -= 1) {
      const v = p50[i];
      if (v !== null && Number.isFinite(v)) {
        latestMedian = v;
        break;
      }
    }

    // Apply repo-wide chart convention: title centered at top, horizontal
    // legend BETWEEN title and chart drawing area, inner ticks. Title lives
    // in Plotly (no DOM h3 wrapper), so do not add `<ChartCard title>` for
    // this chart.
    //
    // Tight L/R margins; top margin bumped to t:70 so the stacked title +
    // legend rows have room and the top y-tick stays clear.
    const titleText = `${shortName(country)} (Latest: ${latestMedian.toFixed(1)}% Ann.)`;
    const chartConv = chartLayout({
      family: 'timeseries',
      title: titleText,
      marginOverride: { l: 35, r: 35, t: 70, b: 30, pad: 2 },
    });
    // The shared `chartLayout` places the title at y:0.98 (anchored
    // `top`, INSIDE the plot) which overlaps the top y-axis tick. Stack it
    // ABOVE the plot: title at y:1.12 / legend at y:1.02 so the order is
    // title -> legend -> plot, with the top tick fully clear. Title font is
    // pinned to size 16 to match legacy `nowcasts.js` (the shared preset
    // defaults to 18).
    const titleAboveLegend =
      chartConv.title && typeof chartConv.title === 'object'
        ? {
            ...chartConv.title,
            font: { ...chartConv.title.font, size: 16 },
            y: 1.12,
            yanchor: 'bottom' as const,
          }
        : chartConv.title;
    // Legacy `nowcasts.js` renders the legend at size 12; the shared preset
    // now defaults to 16, so pin it back to 12.
    const legendBelowTitle =
      chartConv.legend && typeof chartConv.legend === 'object'
        ? {
            ...chartConv.legend,
            y: 1.02,
            yanchor: 'bottom' as const,
            font: { ...(chartConv.legend.font ?? {}), size: 12 },
          }
        : chartConv.legend;
    const composedLayout: Partial<Layout> = {
      ...chartConv,
      title: titleAboveLegend,
      legend: legendBelowTitle,
      xaxis: {
        type: 'date',
        tickformat,
        dtick,
        range: [minDate.toISOString(), xRangeEnd.toISOString()],
        showgrid: true,
        gridcolor: '#DCDCDC',
        gridwidth: 1,
        ...chartConv.xaxis,
      },
      yaxis: {
        ...chartConv.yaxis,
        // y-axis title pinned to size 16. Without an explicit `font.size`,
        // Plotly defaults to 1.2x the layout.font size (19px here), which is
        // visibly too large.
        title: { text: '% Ann.', standoff: 10, font: { size: 16 } },
        range: [yMin, yMax],
        tickformat: '.1f',
        showgrid: true,
        gridcolor: '#DCDCDC',
        gridwidth: 1,
      },
      yaxis2: {
        overlaying: 'y',
        side: 'right',
        showgrid: false,
        range: [yMin, yMax],
        tickformat: '.1f',
        ...AXIS_LINE,
        fixedrange: true,
      },
      shapes: getRecessionShapes({
        plotXRange: [minDate.toISOString(), xRangeEnd.toISOString()],
      }),
    };

    return { traces: assembled, layout: composedLayout };
  }, [processed, country]);

  const handleClick = (event: Readonly<PlotMouseEvent>): void => {
    if (!onPointClick) return;
    const point = event.points?.[0];
    if (!point) return;
    // Identify the Real-Time trace by name; render order no longer pins it
    // to curveNumber 0 (bands and trend now render before the line).
    const traceName = (point.data as { name?: string } | undefined)?.name;
    if (traceName !== REALTIME_TRACE_NAME) return;
    const x = point.x;
    const y = typeof point.y === 'number' ? point.y : null;
    const dateStr =
      typeof x === 'string'
        ? x.slice(0, 10)
        : x instanceof Date
          ? x.toISOString().slice(0, 10)
          : null;
    if (dateStr) {
      onPointClick(dateStr, y);
    }
  };

  if (processed.dates.length === 0) {
    return (
      <div className="flex h-64 items-center justify-center text-sm text-[color:var(--text-dark)] opacity-70">
        No nowcast data available
      </div>
    );
  }

  return (
    <div className="w-full" style={{ height }}>
      <PlotlyChart data={traces} layout={layout} onClick={handleClick} />
    </div>
  );
}
